/*
  nsi-paypal-msg.js: helper for PayPal's Pay Later message boxes (snippets/nsi-paypal-msg.liquid). Andrew, 20 Oct 2026.
  PayPal's own script (Payments Studio, paypal-messaging.js) fetches and renders PayPal's wording into every
  .paypal-messaging box. This file never writes any Pay Later wording. It only:
    1. hides boxes PayPal's script never took (the embed is off, or its script was blocked or failed). PayPal's script is
       a plain script at the end of the page and this one is deferred, so by now it has set up every box it ever will;
    2. swaps in the app's current (versioned) icon and logo URLs from #paypal-drawer-cart-config;
    3. moves the amount when it changes. The product page follows the selected variant (nsi:variant-price, cents, at the
       trade price for trade members). The slide-out cart follows its own trade-aware subtotal: finishRender() in
       nsi-cart-drawer.js writes the cents, a "trade prices still loading" flag and PayPal's digital-goods flag on
       [data-nsi-cd-subtotal]. The cart page reloads after every change, so its box is right from the server.
       Each new amount gets a FRESH box rather than an update: PayPal's script starts a request for every update and
       paints whichever answer lands last, so an update in place could leave an old amount on screen. PayPal's own
       observer sets the new box up and it asks once; a late answer for the old box lands on a box that's gone.
       Where PayPal shows no Pay Later (gift cards and non-shipping lines over 25% of the cart, its own rule) the box
       is kept out of sight with data-nsi-ppm-digital. In the slide-out cart a changed total shows a loading bar at once
       (data-nsi-ppm-stale) rather than the old amount;
    4. sizes PayPal's text so it stays on one line (Andrew: "these things need to fit on 1 line").
*/
(function () {
  'use strict';
  var SEL = '.paypal-messaging[data-nsi-ppm]';
  var INIT = 'data-paypal-messaging-init';
  var cfg = null;
  var ro = 'ResizeObserver' in window ? new window.ResizeObserver(onResize) : null;
  var queued = [];

  function boxes() { return Array.prototype.slice.call(document.querySelectorAll(SEL)); }

  // Largest font size from data-fit-max down to data-fit-min (0.5px steps) that keeps PayPal's sentence on one line.
  function fit(b) {
    var content = b.querySelector('.paypal-messaging__content');
    var text = b.querySelector('.paypal-messaging__text');
    if (!content || !text || b.hidden || !b.offsetWidth || !text.textContent.trim()) return;
    var max = parseFloat(b.getAttribute('data-fit-max')) || 14;
    var min = parseFloat(b.getAttribute('data-fit-min')) || 11;
    for (var fs = max; fs >= min; fs -= 0.5) {
      content.style.setProperty('font-size', fs + 'px', 'important');
      var lh = parseFloat(window.getComputedStyle(content).lineHeight) || fs * 1.4;
      if (text.getBoundingClientRect().height <= lh * 1.25) return;
    }
  }
  function fitAll() { boxes().forEach(fit); }
  // ResizeObserver only reports each frame's final size, so fit() trying sizes inside it can't loop.
  function onResize(entries) {
    entries.forEach(function (en) {
      var b = en.target.closest(SEL);
      if (b && queued.indexOf(b) < 0) queued.push(b);
    });
    window.requestAnimationFrame(function () { var q = queued; queued = []; q.forEach(fit); });
  }

  function applyCfg(b) {
    if (!cfg) return;
    if (cfg.logoUrl) b.setAttribute('data-logo-url', cfg.logoUrl);
    if (cfg.logoWhiteUrl) b.setAttribute('data-logo-url-dark', cfg.logoWhiteUrl);
    var icon = b.querySelector('.paypal-messaging__icon');
    if (icon && cfg.iconUrl && icon.getAttribute('src') !== cfg.iconUrl) icon.setAttribute('src', cfg.iconUrl);
    if (cfg.logoUrl) Array.prototype.forEach.call(b.querySelectorAll('.paypal-messaging__inline-logo'), function (img) {
      if (img.getAttribute('src') !== cfg.logoUrl) img.setAttribute('src', cfg.logoUrl);
    });
  }

  // Re-fit when PayPal writes the text, when its inline logo finishes loading (it has no width until then) and when
  // the box changes size (the slide-out cart opening, a rotated phone, the site font arriving).
  function wire(b) {
    var text = b.querySelector('.paypal-messaging__text');
    if (text) new MutationObserver(function () { window.requestAnimationFrame(function () { fit(b); }); })
      .observe(text, { childList: true, characterData: true, subtree: true });
    b.addEventListener('load', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('paypal-messaging__inline-logo')) fit(b);
    }, true);
    if (ro) { ro.observe(b); if (text) ro.observe(text); }
  }
  function unwire(b) {
    if (!ro) return;
    ro.unobserve(b);
    var text = b.querySelector('.paypal-messaging__text');
    if (text) ro.unobserve(text);
  }

  // Point the box at a new amount (cents) and digital state (see 3 above). Returns the box now on the page.
  function setAmount(b, cents, digital) {
    if (!b || !b.parentNode || !(cents > 0)) return b;
    if (digital) { b.setAttribute('data-nsi-ppm-digital', ''); b.removeAttribute('data-nsi-ppm-stale'); return b; }   // PayPal shows no Pay Later here
    var price = Math.round(cents) / 100;
    if (parseFloat(b.getAttribute('data-price')) === price && b.getAttribute('data-has-digital') !== 'true') {
      b.removeAttribute('data-nsi-ppm-digital');   // same amount PayPal already answered for: just show it again
      b.removeAttribute('data-nsi-ppm-stale');
      return b;
    }
    if (b.getAttribute(INIT) !== 'true') return b;   // PayPal never took this box, so it wouldn't take a new one either
    var n = b.cloneNode(true);
    n.removeAttribute(INIT);
    n.removeAttribute('data-nsi-ppm-digital');
    n.removeAttribute('data-nsi-ppm-stale');
    n.hidden = false;
    n.setAttribute('data-price', String(price));
    n.setAttribute('data-has-digital', 'false');
    var content = n.querySelector('.paypal-messaging__content');
    if (content) content.style.setProperty('font-size', (parseFloat(n.getAttribute('data-fit-max')) || 14) + 'px', 'important');
    var text = n.querySelector('.paypal-messaging__text');
    if (text) text.innerHTML = '<span class="paypal-messaging__skeleton"></span>';   // PayPal's own loading bar
    unwire(b);
    b.parentNode.replaceChild(n, b);
    wire(n);
    // PayPal's observer sets the new box up straight away. If it ever doesn't, an empty logo and a dead (i) would
    // be all that showed, so hide it.
    window.setTimeout(function () { if (n.isConnected && n.getAttribute(INIT) !== 'true') n.hidden = true; }, 3000);
    return n;
  }

  function centsFrom(el) {
    var m = (el && el.textContent || '').replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : -1;
  }

  // Does the variant need no shipping? From the box's own variant list (gift cards are always listed as false).
  function variantDigital(b, id) {
    try {
      var list = JSON.parse(b.getAttribute('data-variants') || '[]');
      for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(id)) return list[i].requires_shipping === false;
    } catch (e) { /* fall through */ }
    return b.getAttribute('data-has-digital') === 'true';
  }

  function init() {
    var cfgEl = document.getElementById('paypal-drawer-cart-config');
    if (cfgEl) { try { cfg = JSON.parse(cfgEl.textContent || '{}'); } catch (e) { cfg = null; } }
    boxes().forEach(function (b) {
      if (b.getAttribute(INIT) !== 'true') {
        // Only `hidden`, so PayPal's own reveal can still show it if its script ever sets it up late.
        b.hidden = true;
        if (!cfgEl) b.setAttribute('data-nsi-ppm-off', '');
      }
      applyCfg(b);
      wire(b);
    });

    // Product page: the selected variant's price (cents), at the trade price for trade members.
    document.addEventListener('nsi:variant-price', function (e) {
      var d = e.detail || {};
      if (!(d.price >= 0)) return;
      var scope = d.scope && d.scope.querySelector ? d.scope : document;
      var now = scope.querySelector(SEL + '[data-nsi-ppm="product"]');
      if (now) {   // a different amount: the loading bar at once, not the old variant's figure (data-nsi-ppm-stale)
        var r0 = parseFloat(now.getAttribute('data-nsi-trade-rate')) || 0;
        var c0 = r0 ? Math.floor((d.price * (100 - r0) + 50) / 100) : d.price;
        if (c0 > 0 && Math.round(parseFloat(now.getAttribute('data-price')) * 100) !== c0 && !variantDigital(now, d.id)) now.setAttribute('data-nsi-ppm-stale', '');
      }
      window.clearTimeout(scope.__nsiPpmT);
      scope.__nsiPpmT = window.setTimeout(function () {
        var b = scope.querySelector(SEL + '[data-nsi-ppm="product"]');
        if (!b) return;
        var rate = parseFloat(b.getAttribute('data-nsi-trade-rate')) || 0;
        var cents = rate ? Math.floor((d.price * (100 - rate) + 50) / 100) : d.price;
        setAmount(b, cents, variantDigital(b, d.id));
      }, 120);
    });

    // Slide-out cart: its own trade-aware subtotal, once the trade prices are known, and only while it's open (it
    // catches up when it opens), so a page view with a closed cart sends PayPal nothing extra.
    var root = document.getElementById('nsi-cart-drawer');
    var sub = document.querySelector('[data-nsi-cd-subtotal]');
    if (root && sub) {
      var dirty = false, t;
      var cents = function () { return sub.hasAttribute('data-nsi-cd-cents') ? parseInt(sub.getAttribute('data-nsi-cd-cents'), 10) : centsFrom(sub); };
      var dbox = function () { return document.querySelector(SEL + '[data-nsi-ppm="drawer"]'); };
      var sync = function () {
        if (root.hidden) { dirty = true; return; }
        dirty = false;
        if (sub.hasAttribute('data-nsi-cd-provisional')) return;   // trade prices still loading: wait for the real total
        var b = dbox(), c = cents();
        if (b && c > 0) setAmount(b, c, sub.getAttribute('data-nsi-cd-digital') === 'true');
      };
      // The moment the subtotal changes, PayPal's line still shows the old amount until the new one is asked for (for a
      // trade member, until the trade prices load). So straight away: a cart PayPal shows no Pay Later on hides the box (its
      // rule doesn't depend on trade prices), and a changed amount shows a loading bar instead of the old figure
      // (data-nsi-ppm-stale, nsi-site.css) until the fresh box arrives.
      var soon = function () {
        var b = dbox(), c = cents();
        if (b && sub.getAttribute('data-nsi-cd-digital') === 'true') setAmount(b, c, true);
        else if (b && c > 0 && Math.round(parseFloat(b.getAttribute('data-price')) * 100) !== c) b.setAttribute('data-nsi-ppm-stale', '');
        window.clearTimeout(t); t = window.setTimeout(sync, 250);
      };
      new MutationObserver(soon).observe(sub, { attributes: true, childList: true, characterData: true, subtree: true });
      new MutationObserver(function () {
        if (!root.hidden && dirty) soon();
        window.setTimeout(fitAll, 60);
      }).observe(root, { attributes: true, attributeFilter: ['hidden', 'class'] });
    }

    var rt;
    window.addEventListener('resize', function () { window.clearTimeout(rt); rt = window.setTimeout(fitAll, 150); });
    // The site's web font is wider than the fallback, so a size that fits before it loads can wrap after.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
    window.addEventListener('load', fitAll);
    fitAll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
